import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { RRIInput } from '../../interfaces/input-settings.interface';

@Component({
  selector: 'rr-color-picker-field',
  templateUrl: './color-picker-field.component.html',
  styleUrls: ['./color-picker-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ColorPickerFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ColorPickerFieldComponent),
      multi: true,
    },
  ],
})
export class ColorPickerFieldComponent
  implements OnInit, ControlValueAccessor, Validator
{
  public data: string;
  public onChangeFn: any = (_: any) => {};
  public onTouch: any = () => {};
  public control: NgControl;
  private errorMessages = new Map<string, () => string>();
  private errorMinlengthDefault: string =
    'The no. of characters should not be less than';
  private errorMaxlengthDefault: string =
    'The no. of characters should not be more than';

  @Input() settings: RRIInput<string>;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() minlength: number = 0;
  @Input() maxlength: number;
  @Input() customErrorMessages: Map<string, () => string>;
  @Output() clearInput: EventEmitter<string> = new EventEmitter();

  constructor(private inj: Injector) {
    this.errorMessages.set('color', () => `Please enter a valid hex color`);
    this.errorMessages.set('minlength', () => this.errorMinlengthDefault);
    this.errorMessages.set('maxlength', () => this.errorMaxlengthDefault);
  }

  ngOnInit() {
    this.control = this.inj.get(NgControl, null);

    if (this.customErrorMessages && this.customErrorMessages.size) {
      this.errorMessages = new Map([
        ...Array.from(this.errorMessages.entries()),
        ...Array.from(this.customErrorMessages.entries()),
      ]);
    }
  }

  public onClearInput(): void {
    if (!this.settings.disableClearEvent) {
      if (this.settings.defaultValue) {
        this.data = this.settings.defaultValue;
      } else {
        this.data = '';
      }
    }
    this.clearInput.emit(this.data);
    this.onChange(this.data);
  }

  public writeValue(text: string): void {
    this.data = text;
  }
  public registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  public onChange(data) {
    this.onChangeFn(data);
  }

  public onTouched() {
    this.onTouch();
  }

  public get invalid(): boolean {
    return this.control ? this.control.invalid : false;
  }

  public get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, value } = this.control;

    return this.invalid ? dirty || touched || !!value : false;
  }

  public get errors(): Array<string> {
    if (!this.control) {
      return [];
    }

    const { errors } = this.control;
    return Object.keys(errors).map((key) => {
      if (
        key == 'minlength' &&
        (typeof this.customErrorMessages == 'undefined' ||
          !this.customErrorMessages.has(key)) &&
        this.errorMessages.has(key)
      ) {
        this.errorMessages.set(
          'minlength',
          () =>
            this.errorMinlengthDefault +
            ' ' +
            String(this.control.errors['minlength']['requiredLength']),
        );
      }
      if (
        key == 'maxlength' &&
        (typeof this.customErrorMessages == 'undefined' ||
          !this.customErrorMessages.has(key)) &&
        this.errorMessages.has(key)
      ) {
        this.errorMessages.set(
          'maxlength',
          () =>
            this.errorMinlengthDefault +
            ' ' +
            String(this.control.errors['maxlength']['requiredLength']),
        );
      }

      return this.errorMessages.has(key)
        ? this.errorMessages.get(key)()
        : 'error: ' + key;
    });
  }

  private validateColor(control: AbstractControl) {
    let value = null;
    if (control && typeof control.value !== 'undefined') {
      value = control.value;
    }

    const re = /^#[0-9A-F]{6}$/i;

    let isNotValid = false;
    if (value) {
      isNotValid = !re.test(String(value).toLowerCase());
    } else {
      if (this.required) {
        isNotValid = true;
      }
    }
    return (
      isNotValid && {
        color: true,
      }
    );
  }
  public getInputControlClasses(): string {
    let returnClass = this.required ? 'fg-required' : '';
    if (this.control) {
      if (typeof this.control.invalid !== 'undefined') {
        returnClass += this.control.invalid ? 'fg-invalid' : 'fg-valid';
      }
    }
    return returnClass;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    return this.validateColor(control);
  }
}
